import { SelectionModel } from '@angular/cdk/collections';
import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
  MatNoDataRow,
} from '@angular/material/table';
import { Router, RouterLink } from '@angular/router';
import { forkJoin, Subject, takeUntil, tap } from 'rxjs';
import {
  Fournisseurs,
  UserByLaboEnseigne,
  User,
} from 'src/app/shared/interfaces/fournisseurs/fournisseurs.model';
import { FournisseurService } from 'src/app/shared/services/fournisseur.service';
import { UpperCasePipe } from '@angular/common';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton, MatIconButton } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import {
  MatFormField,
  MatPrefix,
  MatSuffix,
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatToolbar } from '@angular/material/toolbar';
import { UserKeycloak } from 'src/app/shared/interfaces/keycloak/userKeycloak';

@Component({
  selector: 'app-fournisseurs-user',
  templateUrl: './fournisseurs-user.component.html',
  styleUrl: './fournisseurs-user.component.scss',
  standalone: true,
  imports: [
    MatToolbar,
    RouterLink,
    MatIcon,
    MatFormField,
    MatPrefix,
    MatInput,
    FormsModule,
    MatButton,
    MatIconButton,
    MatSuffix,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatCheckbox,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatNoDataRow,
    MatPaginator,
    UpperCasePipe,
  ],
})
export class FournisseursUserComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private readonly _destroying$ = new Subject<void>();
  // Table
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  dataSource = new MatTableDataSource<UserKeycloak>();
  displayedColumns: string[] = ['lastName', 'firstName', 'email', 'actions'];
  // Filter
  globalFilter = '';
  // list fournisseur
  fournisseur!: Fournisseurs;
  listFournisseursUsers?: UserKeycloak[];
  listUserByLaboEnseigne!: UserByLaboEnseigne[];
  fournisseursSelected = new SelectionModel<UserKeycloak>(true, []);
  // Router data
  undefined = 'undefined';
  routerData: any;

  constructor(
    private router: Router,
    private fournisseurService: FournisseurService
  ) {
    this.routerData = this.router.getCurrentNavigation()?.extras.state;
  }

  ngOnInit(): void {
    this.onTabSelected(0);
    if (typeof this.routerData === this.undefined) {
      this.router.navigate(['/fournisseurs/']);
    } else {
      this.fournisseur = this.routerData.data as Fournisseurs;
      this.fetchAllFournisseur();
      this.fetchAllUserByLaboEnseigne();
    }
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  /**
   * Called when the component is destroyed. Unsubscribes from all subscriptions to prevent memory leaks.
   */
  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  /**
   * Retrieves the list of all suppliers via the fournisseurService service.
   * The data is stored in the listLournisseursBC2 property.
   */
  private fetchAllFournisseur() {
    this.fournisseurService
      .fetchAllFournisseur()
      .pipe(
        takeUntil(this._destroying$),
        tap((data: UserKeycloak[]) => {
          this.listFournisseursUsers = data;
          this.manageDataSourceTable(this.listFournisseursUsers);
          this.selectFournisseurs();
        })
      )
      .subscribe();
  }

  /**
   * Retrieves the list of all User By Labo Enseigne via the fournisseurService service.
   * The data is stored in the listUserByLaboEnseigne property.
   */
  private fetchAllUserByLaboEnseigne() {
    this.fournisseurService
      .fetchAllUserByLaboEnseigne(this.fournisseur.idLaboEnseigne)
      .pipe(
        takeUntil(this._destroying$),
        tap((data: UserByLaboEnseigne[]) => {
          this.listUserByLaboEnseigne = data;
          this.selectFournisseurs();
        })
      )
      .subscribe();
  }

  /**
   * Saves the selected rows as user fournisseurs for the current fournisseur.
   * Transforms the selected rows to UserFournisseurDTO format and sends the data to the server.
   */
  onSave() {
    const selectedRows: UserKeycloak[] = this.fournisseursSelected.selected;
    const oldData: UserKeycloak[] = this.transformedOldData(
      this.listUserByLaboEnseigne,
      this.fournisseur.idLaboEnseigne
    );
    const newToSave: User[] = this.transformedUserFournisseur(
      selectedRows,
      this.fournisseur.idLaboEnseigne
    );
    const { additions, deletions } = this.getAdditionsAndDeletions(
      oldData,
      newToSave
    );
    const dataToDelete: User[] = this.transformedUser(
      deletions,
      this.fournisseur.idLaboEnseigne
    );

    const operations = [];
    if (dataToDelete.length > 0) {
      operations.push(this.fournisseurService.deleteUsers(dataToDelete));
    }
    if (additions.length > 0) {
      operations.push(this.fournisseurService.linkFournisseurToUser(additions));
    }

    if (operations.length > 0) {
      forkJoin(operations)
        .pipe(takeUntil(this._destroying$))
        .subscribe({
          next: () => {
            this.fetchAllUserByLaboEnseigne();
          },
        });
    }
  }

  /**
   * Returns an object with two arrays, containing the additions and deletions between the provided old and new objects.
   * @param {any[]} oldObjects - The array of old objects.
   * @param {UserFournisseurDTO[]} newObjects - The array of new objects.
   * @returns {{ additions: UserFournisseurDTO[]; deletions: any[] }} - An object with two properties:
   * "additions" containing the new objects that were not present in the oldObjects array,
   * and "deletions" containing the old objects that were not present in the newObjects array.
   */
  getAdditionsAndDeletions(
    oldObjects: any[],
    newObjects: User[]
  ): { additions: User[]; deletions: any[] } {
    const additions: User[] = [];
    const deletions: any[] = [];
    for (const oldObject of oldObjects) {
      if (
        !newObjects.find(
          (newObject) =>
            oldObject.idUser === newObject.idUser &&
            oldObject.idLaboEnseigne === newObject.idLaboEnseigne
        )
      ) {
        deletions.push(oldObject);
      }
    }
    for (const newObject of newObjects) {
      if (
        !oldObjects.find(
          (oldObject) =>
            newObject.idUser === oldObject.idUser &&
            newObject.idLaboEnseigne === oldObject.idLaboEnseigne
        )
      ) {
        additions.push(newObject);
      }
    }
    return { additions, deletions };
  }


  /**
   * Transforms the given data array of UserKeycloak that are Fournisseurs objects into an array of UserFournisseurDTO objects.
   * @param data - The array of UserKeycloak that are Fournisseurs objects to transform.
   * @param idLaboEnseigne - The id of the labo enseigne for the transformed UserFournisseurDTO objects.
   * @returns An array of UserFournisseurDTO objects transformed from the given data array.
   */
  transformedUserFournisseur(data: UserKeycloak[], idLaboEnseigne: number) {
    return data.map((Fournisseurs: UserKeycloak) => ({
      id: 0,
      idUser: Fournisseurs.id,
      adresseMail: Fournisseurs.email,
      telephone: '',
      idLaboEnseigne: idLaboEnseigne,
    }));
  }

  /**
   * Transforms an array of "UserByLaboEnseigne" objects by mapping each object to a new User object
   * @param data An array of "UserByLaboEnseigne" objects to transform
   * @param idLaboEnseigne ID
   * @returns An array of transformed objects with the specified properties
   */
  transformedOldData(
    data: UserByLaboEnseigne[],
    idLaboEnseigne: number
  ): UserKeycloak[] {
    return data.map((userByLaboEnseigne: UserByLaboEnseigne) => ({
      id: userByLaboEnseigne.idUser,
      idAzure: '0',
      idUser: userByLaboEnseigne.idUser,
      email: userByLaboEnseigne.adresseMail,
      telephone: userByLaboEnseigne.telephone,
      idLaboEnseigne: idLaboEnseigne,
      firstName: '',
      lastName: '',
      roles: [],
      groups: [],
      enabled: true,
      isInactivated: false,
    }));
  }

  /**
   * Transforms an array of "UserKeycloak" objects by mapping each object to a new User object
   * @param data An array of "UserKeycloak" objects to transform
   * @param idLaboEnseigne  ID
   * @returns An array of transformed objects with the specified properties
   */
  transformedUser(data: UserKeycloak[], idLaboEnseigne: number) {
    return data.map((Fournisseurs: UserKeycloak) => ({
      id: 0,
      idUser: Fournisseurs.id,
      adresseMail: Fournisseurs.email,
      telephone: '',
      idLaboEnseigne: idLaboEnseigne,
    }));
  }

  /**
   * Sets the array of data as the source for a data table called "dataSource".
   * @param data An array of data to set as the source for the data table.
   */
  private manageDataSourceTable(data: UserKeycloak[]) {
    if (data) {
      this.dataSource.data = data;
    }
  }

  /**
   * Handles the selection of an element by toggling its state.
   * @param element The element to select/deselect.
   */
  selectHandler(element: UserKeycloak) {
    this.fournisseursSelected.toggle(element);
  }

  /**
   * Selects the suppliers associated with users from the same group as the current user.
   * The selected suppliers will be stored in the fournisseursSelected property.
   * @returns void
   */
  selectFournisseurs(): void {
    if (this.listUserByLaboEnseigne && this.listFournisseursUsers) {
      this.fournisseursSelected.clear();
      for (const fournisseur of this.listFournisseursUsers) {
        const user = this.listUserByLaboEnseigne.find(
          (u) => u.adresseMail === fournisseur.email
        );
        if (user) {
          this.fournisseursSelected.select(fournisseur);
        }
      }
    }
  }

  /**
   * Manages the filters to apply for the search bar
   * If the table has pagination then the pagination is placed on the first page
   * @param filter The value of the applied filter
   */
  applyFilter(filter: string) {
    this.globalFilter = filter;
    this.dataSource.filter = this.globalFilter;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  /**
   * Highlights the selected tab and sets the others to the default state
   * @param {number} index - The index of the selected tab
   */
  onTabSelected(index: number): void {
    const menuElements = document.querySelectorAll('.menu-element');
    if (menuElements.length > 3 && index >= 0 && index < menuElements.length) {
      for (let i = 0; i < menuElements.length; i++) {
        const menuElement = menuElements[i] as HTMLElement;
        if (i === index) {
          menuElement.classList.remove('menu-element--default');
          menuElement.classList.add('menu-element--highlighted');
        } else {
          menuElement.classList.remove('menu-element--highlighted');
          menuElement.classList.add('menu-element--default');
        }
      }
    }
  }
}
